import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Smartphone, Loader2 } from "lucide-react";

/**
 * MSG91OtpButton — mobile-OTP login for travellers.
 *
 * The MSG91 widget owns the entire OTP UI (phone input, SMS send, verification).
 * On widget success we receive a JWT access-token which the backend verifies
 * against MSG91's server API before issuing our own SwaanStay session token.
 */
export default function MSG91OtpButton({ intent = "traveller", label, redirectTo, mfaToken, onVerified, mode = "login", signupDetails, disabled: extDisabled }) {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [config, setConfig] = useState(null);
  const configuredRef = useRef(false);
  // MSG91 access-tokens are single-use. Guard against the widget calling
  // `success` twice (StrictMode / iOS Safari re-emits) which would trigger a
  // second backend POST with the now-consumed token → confusing "ref 701" toast.
  const usedTokenRef = useRef(null);
  const is2fa = intent === "2fa";
  const endpoint = is2fa
    ? "/auth/verify-2fa"
    : intent === "partner" ? "/auth/msg91/verify-partner" : "/auth/msg91/verify";
  const defaultLabel = is2fa
    ? "Verify OTP to continue"
    : intent === "partner" ? "Signup / Login as Partner (Mobile OTP)" : "Login with Mobile OTP";

  // 1. Fetch widget config from backend (never hardcode secrets in the bundle)
  const [configError, setConfigError] = useState(false);
  const fetchConfig = () => {
    setConfigError(false);
    api
      .get("/auth/msg91/config")
      .then(({ data }) => {
        if (data?.enabled) setConfig(data);
        else setConfigError(true);
      })
      .catch((e) => { setConfigError(true); console.debug("MSG91 config fetch failed:", e?.message); });
  };
  useEffect(() => { fetchConfig(); }, []);

  // 2. Load the MSG91 script exactly once (React 18 Strict-Mode safe)
  useEffect(() => {
    if (!config) return;
    const scriptId = "msg91-otp-provider";
    if (document.getElementById(scriptId)) {
      if (window.initSendOTP) setReady(true);
      return;
    }
    const urls = ["https://verify.msg91.com/otp-provider.js", "https://verify.phone91.com/otp-provider.js"];
    let i = 0;
    const tryLoad = () => {
      const s = document.createElement("script");
      s.id = scriptId;
      s.src = urls[i];
      s.async = true;
      s.onload = () => {
        if (window.initSendOTP) setReady(true);
      };
      s.onerror = () => {
        s.remove();
        i += 1;
        if (i < urls.length) tryLoad();
      };
      document.head.appendChild(s);
    };
    tryLoad();
  }, [config]);

  const handleClick = () => {
    if (!config || !window.initSendOTP || configuredRef.current) return;
    configuredRef.current = true;
    usedTokenRef.current = null; // fresh OTP session — allow one post
    setBusy(true);

    const configuration = {
      widgetId: config.widget_id,
      tokenAuth: config.token_auth,
      exposeMethods: false,
      success: async (data) => {
        try {
          // MSG91 nests the JWT in `data.message` on success
          const accessToken =
            (typeof data === "string" && data) ||
            data?.message ||
            data?.["access-token"] ||
            data?.token ||
            "";
          if (!accessToken) throw new Error("No access-token from OTP widget");
          // Single-use guard: if the widget calls us twice with the same token
          // (has happened on iOS Safari + React StrictMode), skip the second
          // POST silently — the first one is already resolving.
          if (usedTokenRef.current === accessToken) {
            console.debug("[MSG91] duplicate success callback for same token — ignoring");
            return;
          }
          usedTokenRef.current = accessToken;
          const payload = is2fa
            ? { mfa_token: mfaToken, access_token: accessToken }
            : { access_token: accessToken, mode, signup_details: signupDetails || undefined };
          const { data: res } = await api.post(endpoint, payload);
          if (res?.session_token && res?.user_id) {
            localStorage.setItem("hw_session_token", res.session_token);
            setUser(res);
            toast.success(`Welcome, ${res.name?.split(" ")[0] || "friend"}!`);
            if (onVerified) onVerified(res);
            // Route by role: pending partners land on the locked onboarding page.
            const target = redirectTo || (
              res.role === "hotel_partner_pending" ? "/partner/onboarding"
              : res.role === "hotel_partner" ? "/partner"
              : res.role === "admin" ? "/admin"
              : "/customer"
            );
            navigate(target);
          }
        } catch (err) {
          toast.error(err?.response?.data?.detail || "Could not sign you in. Please try again.");
        } finally {
          setBusy(false);
          configuredRef.current = false;
        }
      },
      failure: (err) => {
        setBusy(false);
        configuredRef.current = false;
        // Log the raw payload so support can diagnose (visible in devtools only)
        console.debug("[MSG91] widget failure payload:", err);
        if (!err || err.type === "closed") return; // user just closed the modal
        const message = err?.message || err?.error || "OTP verification cancelled.";
        // Common MSG91 hint: if the widget rejects with 'Authentication Failed', it's
        // usually a domain-whitelist or widget-config issue on the MSG91 dashboard side.
        if (/authentication\s*fail/i.test(String(message))) {
          toast.error("OTP verification failed. Our team has been notified — please try Google login or email/password for now.");
        } else {
          toast.error(message);
        }
      },
    };

    try {
      window.initSendOTP(configuration);
    } catch (e) {
      setBusy(false);
      configuredRef.current = false;
      toast.error("Could not launch OTP widget. Please refresh and try again.");
    }
  };

  if (!config) {
    // Never silently vanish — the button was previously hidden until config
    // loaded, which looked like a broken login page on production if the
    // config endpoint failed or MSG91 was disabled. Show a visible placeholder
    // + retry link instead.
    return (
      <div className="w-full">
        <Button
          type="button"
          disabled
          className="w-full bg-emerald-600/50 text-white rounded-sm font-bold h-11"
          data-testid="msg91-otp-login-btn"
        >
          <Loader2 className="w-4 h-4 animate-spin mr-2" />
          {configError ? "OTP unavailable — retry" : "Loading OTP…"}
        </Button>
        {configError && (
          <button
            type="button"
            onClick={fetchConfig}
            className="mt-2 w-full text-center text-xs text-blue-700 hover:underline"
            data-testid="msg91-otp-retry"
          >
            Tap to retry
          </button>
        )}
      </div>
    );
  }

  return (
    <Button
      type="button"
      onClick={handleClick}
      disabled={!ready || busy || extDisabled}
      className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm font-bold h-11"
      data-testid="msg91-otp-login-btn"
    >
      {busy ? (
        <Loader2 className="w-4 h-4 animate-spin mr-2" />
      ) : (
        <Smartphone className="w-4 h-4 mr-2" />
      )}
      {busy ? "Opening OTP…" : (label || defaultLabel)}
    </Button>
  );
}
